<template>
  <page-wrapper error-key="user" :breadcrumbs="breadcrumbs" transparent-bg>
    <template #title>
      <skeleton-loader v-if="!user" width="350px" :height="$vuetify.display.mdAndDown ? '48px' : '52px'" />
      <div v-else class="h3">{{ userFullName }}</div>
      <v-spacer />
      <delete-user-confirmation-dialog
        v-if="user && !user.isOwner"
        :user-info="{
          id: userId,
          name: user.profile.name,
          lastName: user.profile.lastName,
          middleName: user.profile.middleName,
        }"
        @deleted="onDelete"
      >
        <template #activator>
          <e-btn :loading="isDeleteLoading" variant="ghost" class="text-red">
            <template v-if="!$vuetify.display.xs" #prepend>
              <e-icon name="trash" />
            </template>
            <span v-if="!$vuetify.display.xs">Удалить</span>
            <e-icon v-else name="trash" />
          </e-btn>
        </template>
      </delete-user-confirmation-dialog>
    </template>

    <page-block>
      <property-block content-max-width="800px">
        <template #text>
          <skeleton-loader v-if="!user" width="200px" height="200px" type="round" class="ma-auto" />
          <avatar
            v-else
            class="ma-auto"
            :avatar="user.profile.userAvatar"
            :literal="user.profile.name.slice(0, 2)"
            show-error
            size="XXL"
          />
        </template>
        <template #content>
          <main-user-info class="mt-6" :user="user" />
        </template>
      </property-block>
    </page-block>

    <page-block class="mt-4" full-page>
      <property-block>
        <template #text>
          <div class="h4 text-secondary">Доступы</div>
        </template>
        <template #content>
          <skeleton-loader v-if="!user" width="100%" height="52px" />
          <e-select
            v-else
            label="Роль в пространстве"
            item-value="value"
            :model-value="displayRole"
            :items="USER_ROLE_SELECT_ITEMS"
            :disabled="user.role === Roles.Support || user.isOwner"
            :loading="isLoadingRoleUpdate"
            @update:model-value="handleUserRoleUpdate"
          />
        </template>
      </property-block>
    </page-block>
  </page-wrapper>
</template>

<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue';
import { useRoute } from 'vue-router';
import type { BreadcrumbsItem } from '@/core/types/components/helps';
import type { UserInfo } from '@/parts/users/types/users';
import Avatar from '@/core/components/common/Avatar.vue';
import PageBlock from '@/core/components/interface/PageBlock.vue';
import PageWrapper from '@/core/components/interface/PageWrapper.vue';
import PropertyBlock from '@/core/components/interface/PropertyBlock.vue';
import SkeletonLoader from '@/core/components/interface/SkeletonLoader.vue';
import { EBtn, EIcon, ESelect } from '@/core/components/ui';
import { useFetch } from '@/core/composables/useFetch';
import { useRouterBack } from '@/core/composables/useRouterBack';
import { RouteNames } from '@/core/plugins/router/routeNames';
import DeleteUserConfirmationDialog from '@/parts/users/components/DeleteUserConfirmationDialog.vue';
import MainUserInfo from '@/parts/users/components/MainUserInfo.vue';
import { useUsersStore } from '@/parts/users/store/users';
import { Roles, type RolesType, USER_ROLE_SELECT_ITEMS } from '@/parts/users/utils/consts';

const { routerBack } = useRouterBack();

const $route = useRoute();
const { getUser, setUserRole } = useUsersStore();

const userId = toRef(() => String($route.params.id));

const isDeleteLoading = ref(false);

const displayRole = computed(() => {
  if (!user.value) return undefined;
  return user.value.isOwner ? Roles.Owner : user.value.role;
});

const onDelete = () => {
  routerBack({
    name: RouteNames.Users,
  });
};

const user = ref<UserInfo>();

const getUserInfo = async () => {
  const result = await getUser(userId.value);

  if (result?.data) {
    user.value = result.data.user;
  }
};

watch(userId, getUserInfo);

const isLoadingRoleUpdate = ref(false);

const handleUserRoleUpdate = async (role: RolesType | null | undefined) => {
  if (!user.value || !role || role === Roles.Owner) {
    return;
  }

  isLoadingRoleUpdate.value = true;

  const result = await setUserRole({ role, userId: user.value.id });

  if (!result.value?.errors) {
    user.value.role = role;
  }

  isLoadingRoleUpdate.value = false;
};

const userFullName = toRef(() =>
  user.value
    ? `${user.value.profile.lastName} ${user.value.profile.name} ${user.value.profile.middleName ? user.value.profile.middleName : ''}`
    : '',
);

const breadcrumbs = toRef<() => BreadcrumbsItem[]>(() => [
  {
    icon: 'users3',
    title: 'Пользователи',
    to: {
      name: RouteNames.Users,
    },
  },
  {
    title: userFullName.value,
  },
]);

useFetch(getUserInfo);
</script>
